// ---------------------------------------------------------------------------
// Size-chart initialiser for Bont tool-tips
// Listens for the custom 'tooltip:open' event emitted by <tool-tip-trigger>.
// When the event fires, we wait briefly for the pop-over element to be added
// to the DOM, locate the newly opened pop-over, determine which chart type
// is requested (via a data attribute on your markup), and then delegate the
// actual rendering to your existing helper, initialiseChartByType().
// ---------------------------------------------------------------------------

/* ---------------------------------------------------------------------------
   Bont Size Chart – two-stage calculator hook-up
   ① Waits for the <tool-tip data-tool-tip="…"> element to appear
   ② Once present, waits for the child node that carries [data-chart-type]
--------------------------------------------------------------------------- */
document.addEventListener('tooltip:open', e => {
  const ctx = e.detail.context;      // e.g. "size-chart"
  let popover = null;

  /* 1️⃣  Global observer – watches <body> until the <tool-tip> is injected */
  const popoverObserver = new MutationObserver((_, obs) => {
    popover = document.querySelector(`tool-tip[data-tool-tip="${ctx}"]`);
    if (!popover) return;            // element not in the DOM yet

    obs.disconnect();                // found the pop-over – stop observing
    watchContent();                  // move to stage ②
  });

  popoverObserver.observe(document.body, { childList: true, subtree: true });

  /* 2️⃣  With the <tool-tip> in place, wait for the size-chart markup */
  function watchContent() {
    const tryInit = () => {
      const container = popover.querySelector('[data-chart-type]');
      if (!container) return false;  // content not injected yet

      const chartType = container.getAttribute('data-chart-type');
      console.log('[BONT] Initialising chartType →', chartType, container);
      initializeChartByType(chartType, popover);
      return true;
    };

    /* Immediate attempt (handles cases where content is already present) */
    if (tryInit()) return;

    /* Fallback: observe until the theme copies the HTML into the pop-over */
    const contentObserver = new MutationObserver((_, obs2) => {
      if (tryInit()) obs2.disconnect();
    });
    contentObserver.observe(popover, { childList: true, subtree: true });
  }
});



// Bont Size Charts JavaScript (Enhanced Version for Modal & Page Support)
// Automatically loads correct calculator based on page URL or data attributes

// --- Configuration ---
const CHART_TYPES = {
  SLIPSTREAM_VAYPOR: 'slipstream-vaypor',
  ECLIPSE: 'eclipse',
  PARKSTAR_PROSTAR: 'parkstar-prostar',
  GENERAL_INLINE: 'general-inline',
  SIZE_CHART: 'size-chart',
  SPIN_BOOT: 'spin-boot'
};

// --- Page Router ---
document.addEventListener("DOMContentLoaded", function () {
  initializeCalculators();

  // Set up modal observers for dynamic content
  observeModalOpening();
});

function initializeCalculators() {
  const url = window.location.pathname;

  // Check for dedicated size chart pages first
  if (url.includes("bont-size-chart-slipstream-and-vaypor-boots")) {
    initSlipstreamVaypor();
  } else if (url.includes("bont-size-chart-eclipse-boot")) {
    initEclipse();
  } else if (url.includes("bont-size-chart-parkstar-prostar")) {
    initParkstarProstar();
  } else if (url.includes("bont-size-chart-all-other-inline-and-roller-skate-boots")) {
    initGeneralInlineRoller();
  } else if (url.includes("bont-size-chart-spin-boot")) {
    initSpinBoot();
  }

  // Check for modals or embedded charts with data attributes
  initializeEmbeddedCharts();
}

function initializeEmbeddedCharts() {
  // Look for containers with chart type data attributes
  const chartContainers = document.querySelectorAll('[data-chart-type]');

  chartContainers.forEach(container => {
    const chartType = container.getAttribute('data-chart-type');
    initializeChartByType(chartType, container);
  });

  // Fallback: detect chart type from page content or meta fields
  detectChartTypeFromContent();
}

function initializeChartByType(chartType, container = document) {
  switch (chartType) {
    case CHART_TYPES.SLIPSTREAM_VAYPOR:
      initSlipstreamVaypor(container);
      break;
    case CHART_TYPES.ECLIPSE:
      initEclipse(container);
      break;
    case CHART_TYPES.PARKSTAR_PROSTAR:
      initParkstarProstar(container);
      break;
    case CHART_TYPES.GENERAL_INLINE:
    case CHART_TYPES.SIZE_CHART:
      initGeneralInlineRoller(container);
      break;
    case CHART_TYPES.SPIN_BOOT:
      initSpinBoot(container);
      break;
  }
}

function detectChartTypeFromContent() {
  // Check for Shopify metafield data or page references
  const sizeChartLinks = document.querySelectorAll('modal-opener[data-modal*="SizeChart"]');

  sizeChartLinks.forEach(link => {
    const button = link.querySelector('button');
    if (button) {
      const buttonText = button.textContent.toLowerCase();
      let chartType = null;

      if (buttonText.includes('slipstream') || buttonText.includes('vaypor')) {
        chartType = CHART_TYPES.SLIPSTREAM_VAYPOR;
      } else if (buttonText.includes('eclipse')) {
        chartType = CHART_TYPES.ECLIPSE;
      } else if (buttonText.includes('parkstar') || buttonText.includes('prostar')) {
        chartType = CHART_TYPES.PARKSTAR_PROSTAR;
      } else if (buttonText.includes('spin')) {
        chartType = CHART_TYPES.SPIN_BOOT;
      } else if (buttonText.includes('inline') || buttonText.includes('roller')) {
        chartType = CHART_TYPES.GENERAL_INLINE;
      }

      if (chartType) {
        // Store chart type for modal initialization
        link.setAttribute('data-chart-type', chartType);
      }
    }
  });
}

function observeModalOpening() {
  // Observe for modal opening events
  const modalObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1 && node.classList && node.classList.contains('modal')) {
          // Modal has been added, check if it contains a size chart
          const chartType = getChartTypeFromModal(node);
          if (chartType) {
            setTimeout(() => initializeChartByType(chartType, node), 100);
          }
        }
      });
    });
  });

  modalObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Also listen for modal open events
  document.addEventListener('click', function(e) {
    const modalOpener = e.target.closest('modal-opener');
    if (modalOpener) {
      const chartType = modalOpener.getAttribute('data-chart-type');
      if (chartType) {
        setTimeout(() => {
          const modal = document.querySelector(modalOpener.getAttribute('data-modal'));
          if (modal) {
            initializeChartByType(chartType, modal);
          }
        }, 200);
      }
    }
  });
}

function getChartTypeFromModal(modal) {
  // Attempt to determine chart type from modal content
  const modalContent = modal.textContent.toLowerCase();

  if (modalContent.includes('slipstream') || modalContent.includes('vaypor')) {
    return CHART_TYPES.SLIPSTREAM_VAYPOR;
  } else if (modalContent.includes('eclipse')) {
    return CHART_TYPES.ECLIPSE;
  } else if (modalContent.includes('parkstar') || modalContent.includes('prostar')) {
    return CHART_TYPES.PARKSTAR_PROSTAR;
  } else if (modalContent.includes('spin')) {
    return CHART_TYPES.SPIN_BOOT;
  } else if (modalContent.includes('inline') || modalContent.includes('roller')) {
    return CHART_TYPES.GENERAL_INLINE;
  }

  return null;
}

// --- Shared Handlers ---
function setupHandlers(runFn, container = document) {
  // Legacy markup: <div class="goButton"><a>...</a></div>
  const goButtonAnchor = container.querySelector(".goButton a");
  if (goButtonAnchor) {
    goButtonAnchor.addEventListener("click", function (e) {
      e.preventDefault();
      runFn(container);
    });
  }

  // New markup: <div class="goButton"><button>...</button></div>
  // Also covers any explicit #calculate-size-btn button.
  const goButtonNative = container.querySelector(".goButton button, #calculate-size-btn");
  if (goButtonNative) {
    goButtonNative.addEventListener("click", function (e) {
      e.preventDefault();
      runFn(container);
    });
  }

  const formInputs = container.querySelectorAll(".form-text");
  formInputs.forEach(input => {
    input.addEventListener("keypress", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        runFn(container);
      }
    });
  });

  const tableCells = container.querySelectorAll(".bont-size-chart-table td");
  tableCells.forEach(cell => {
    cell.addEventListener("mouseenter", () => {
      const table = cell.closest("table");
      const index = cell.cellIndex;

      table.querySelectorAll("td, th").forEach(el => el.classList.remove("highlight-col", "highlight-row"));
      table.querySelectorAll("tr").forEach(row => {
        if (row.cells.length > index) row.cells[index].classList.add("highlight-col");
      });
      cell.parentElement.querySelectorAll("td").forEach(td => td.classList.add("highlight-row"));
    });

    cell.addEventListener("mouseleave", () => {
      const table = cell.closest("table");
      table.querySelectorAll("td, th").forEach(el => el.classList.remove("highlight-col", "highlight-row"));
    });
  });
}

// --- Shared utility ---
// Generic width classifier for boots that have any of: narrow / standard / wide / doubleWide
// Order of preference (per spec): Narrow → Standard → Wide → Double Wide.
function classifyWidthByFits(width, sizeRow) {
  if (width === null || isNaN(width)) return "Not specified";
  if (!sizeRow || sizeRow.size === "N/A") return "Custom";

  const hasAnyFit = sizeRow.narrow || sizeRow.standard || sizeRow.wide || sizeRow.doubleWide;
  if (!hasAnyFit) return "Not available for this size";

  const inRange = (w, range) => range && w >= range[0] && w <= range[1];

  if (inRange(width, sizeRow.narrow))     return "Narrow";
  if (inRange(width, sizeRow.standard))   return "Standard";
  if (inRange(width, sizeRow.wide))       return "Wide";
  if (inRange(width, sizeRow.doubleWide)) return "Double Wide";
  return "Custom";
}

// --- Chart 1: Slipstream & Vaypor ---
function initSlipstreamVaypor(container = document) {
  setupHandlers(runSlipstreamVaypor, container);
}

function runSlipstreamVaypor(container = document) {
  const leftInput = container.querySelector("#left-foot-length");
  const rightInput = container.querySelector("#right-foot-length");
  const leftResult = container.querySelector(".left-bont");
  const rightResult = container.querySelector(".right-bont");

  if (!leftInput || !rightInput || !leftResult || !rightResult) return;

  const left = getBasicSize(+leftInput.value);
  const right = getBasicSize(+rightInput.value);

  leftResult.textContent = `${left.size}`;
  rightResult.textContent = `${right.size}`;
}

function getBasicSize(length) {
  const chart = [
    { min: 176, max: 180, size: "180" }, { min: 181, max: 185, size: "185" },
    { min: 186, max: 190, size: "190" }, { min: 191, max: 195, size: "195" },
    { min: 196, max: 200, size: "200" }, { min: 201, max: 205, size: "205" },
    { min: 206, max: 210, size: "210" }, { min: 211, max: 215, size: "215" },
    { min: 216, max: 220, size: "220" }, { min: 221, max: 225, size: "225" },
    { min: 226, max: 230, size: "230" }, { min: 231, max: 235, size: "235" },
    { min: 236, max: 240, size: "240" }, { min: 241, max: 245, size: "245" },
    { min: 246, max: 250, size: "250" }, { min: 251, max: 255, size: "255" },
    { min: 256, max: 260, size: "260" }, { min: 261, max: 265, size: "265" },
    { min: 266, max: 270, size: "270" }, { min: 271, max: 275, size: "275" },
    { min: 276, max: 280, size: "280" }, { min: 281, max: 285, size: "285" },
    { min: 286, max: 290, size: "290" }, { min: 291, max: 295, size: "295" },
    { min: 296, max: 300, size: "300" }, { min: 301, max: 305, size: "305" },
    { min: 306, max: 310, size: "310" }, { min: 311, max: 315, size: "315" }
  ];
  return chart.find(r => length >= r.min && length <= r.max) || { size: "N/A" };
}

// --- Chart 2: Eclipse ---
function initEclipse(container = document) {
  setupHandlers(runEclipse, container);
}

function runEclipse(container = document) {
  const leftLengthInput  = container.querySelector("#left-foot-length");
  const rightLengthInput = container.querySelector("#right-foot-length");
  const leftWidthInput   = container.querySelector("#left-foot-width");
  const rightWidthInput  = container.querySelector("#right-foot-width");

  const leftBont  = container.querySelector(".left-bont");
  const rightBont = container.querySelector(".right-bont");
  const leftWidth  = container.querySelector(".left-width");
  const rightWidth = container.querySelector(".right-width");

  if (!leftLengthInput || !rightLengthInput || !leftBont || !rightBont) return;

  const left  = getEclipseSize(+leftLengthInput.value);
  const right = getEclipseSize(+rightLengthInput.value);

  leftBont.textContent  = `${left.size}`;
  rightBont.textContent = `${right.size}`;

  if (leftWidth) {
    const w = leftWidthInput && leftWidthInput.value !== "" ? +leftWidthInput.value : null;
    leftWidth.textContent = classifyEclipseWidth(w, left);
  }
  if (rightWidth) {
    const w = rightWidthInput && rightWidthInput.value !== "" ? +rightWidthInput.value : null;
    rightWidth.textContent = classifyEclipseWidth(w, right);
  }
}

function classifyEclipseWidth(width, sizeRow) {
  if (width === null || isNaN(width)) return "Not specified";
  if (!sizeRow || sizeRow.size === "N/A") return "Custom";

  const { standardMin, standardMax, wideMin, wideMax } = sizeRow;

  if (width >= standardMin && width <= standardMax) return "Standard";
  if (width >= wideMin && width <= wideMax) return "Wide";
  return "Custom";
}

function getEclipseSize(length) {
  const chart = [
    { min: 175, max: 179, size: "180", standardMin: 65,  standardMax: 69,  wideMin: 69,  wideMax: 73  },
    { min: 180, max: 184, size: "185", standardMin: 68,  standardMax: 72,  wideMin: 72,  wideMax: 76  },
    { min: 185, max: 189, size: "190", standardMin: 69,  standardMax: 73,  wideMin: 73,  wideMax: 77  },
    { min: 190, max: 194, size: "195", standardMin: 70,  standardMax: 74,  wideMin: 74,  wideMax: 78  },
    { min: 195, max: 199, size: "200", standardMin: 71,  standardMax: 75,  wideMin: 75,  wideMax: 79  },
    { min: 200, max: 204, size: "205", standardMin: 74,  standardMax: 78,  wideMin: 78,  wideMax: 82  },
    { min: 205, max: 209, size: "210", standardMin: 76,  standardMax: 85,  wideMin: 80,  wideMax: 89  },
    { min: 210, max: 213, size: "215", standardMin: 77,  standardMax: 86,  wideMin: 81,  wideMax: 90  },
    { min: 214, max: 219, size: "220", standardMin: 79,  standardMax: 88,  wideMin: 83,  wideMax: 92  },
    { min: 220, max: 223, size: "225", standardMin: 80,  standardMax: 89,  wideMin: 84,  wideMax: 93  },
    { min: 224, max: 229, size: "230", standardMin: 82,  standardMax: 91,  wideMin: 86,  wideMax: 95  },
    { min: 230, max: 233, size: "235", standardMin: 84,  standardMax: 94,  wideMin: 88,  wideMax: 98  },
    { min: 234, max: 239, size: "240", standardMin: 85,  standardMax: 95,  wideMin: 89,  wideMax: 99  },
    { min: 240, max: 244, size: "245", standardMin: 87,  standardMax: 96,  wideMin: 91,  wideMax: 100 },
    { min: 245, max: 250, size: "250", standardMin: 88,  standardMax: 98,  wideMin: 92,  wideMax: 102 },
    { min: 251, max: 254, size: "255", standardMin: 89,  standardMax: 99,  wideMin: 93,  wideMax: 103 },
    { min: 255, max: 260, size: "260", standardMin: 90,  standardMax: 101, wideMin: 94,  wideMax: 105 },
    { min: 261, max: 264, size: "265", standardMin: 92,  standardMax: 102, wideMin: 96,  wideMax: 106 },
    { min: 265, max: 270, size: "270", standardMin: 93,  standardMax: 103, wideMin: 97,  wideMax: 107 },
    { min: 271, max: 274, size: "275", standardMin: 95,  standardMax: 105, wideMin: 99,  wideMax: 109 },
    { min: 275, max: 280, size: "280", standardMin: 97,  standardMax: 108, wideMin: 101, wideMax: 112 },
    { min: 281, max: 284, size: "285", standardMin: 98,  standardMax: 109, wideMin: 102, wideMax: 113 },
    { min: 285, max: 290, size: "290", standardMin: 99,  standardMax: 110, wideMin: 103, wideMax: 114 },
    { min: 291, max: 294, size: "295", standardMin: 100, standardMax: 112, wideMin: 104, wideMax: 116 },
    { min: 295, max: 300, size: "300", standardMin: 102, standardMax: 113, wideMin: 106, wideMax: 117 },
    { min: 301, max: 304, size: "305", standardMin: 104, standardMax: 116, wideMin: 108, wideMax: 118 },
    { min: 305, max: 310, size: "310", standardMin: 105, standardMax: 117, wideMin: 109, wideMax: 119 },
    { min: 311, max: 314, size: "315", standardMin: 107, standardMax: 119, wideMin: 111, wideMax: 123 }
  ];
  return chart.find(r => length >= r.min && length <= r.max) || { size: "N/A" };
}

// --- Chart 3: Parkstar / Prostar / Quadstar ---
function initParkstarProstar(container = document) {
  setupHandlers(runParkstarProstar, container);
}

function runParkstarProstar(container = document) {
  const leftLengthInput  = container.querySelector("#left-foot-length");
  const rightLengthInput = container.querySelector("#right-foot-length");
  const leftWidthInput   = container.querySelector("#left-foot-width");
  const rightWidthInput  = container.querySelector("#right-foot-width");

  const leftBont  = container.querySelector(".left-bont");
  const rightBont = container.querySelector(".right-bont");
  const leftEuro  = container.querySelector(".left-euro");
  const rightEuro = container.querySelector(".right-euro");
  const leftWidth  = container.querySelector(".left-width");
  const rightWidth = container.querySelector(".right-width");

  if (!leftLengthInput || !rightLengthInput || !leftBont || !rightBont) return;

  const left  = getParkstarSize(+leftLengthInput.value);
  const right = getParkstarSize(+rightLengthInput.value);

  leftBont.textContent  = `${left.size}`;
  rightBont.textContent = `${right.size}`;

  if (leftEuro)  leftEuro.textContent  = `${left.euro}`;
  if (rightEuro) rightEuro.textContent = `${right.euro}`;

  if (leftWidth) {
    const w = leftWidthInput && leftWidthInput.value !== "" ? +leftWidthInput.value : null;
    leftWidth.textContent = classifyWidthByFits(w, left);
  }
  if (rightWidth) {
    const w = rightWidthInput && rightWidthInput.value !== "" ? +rightWidthInput.value : null;
    rightWidth.textContent = classifyWidthByFits(w, right);
  }
}

function getParkstarSize(length) {
  const chart = [
    { min: 174, max: 182, size: "10C",   euro: "28",   narrow: null,        standard: null,        wide: null,         doubleWide: null         },
    { min: 183, max: 190, size: "11C",   euro: "29",   narrow: null,        standard: null,        wide: null,         doubleWide: null         },
    { min: 191, max: 198, size: "12C",   euro: "30",   narrow: null,        standard: null,        wide: null,         doubleWide: null         },
    { min: 199, max: 202, size: "13C",   euro: "31",   narrow: null,        standard: null,        wide: null,         doubleWide: null         },
    { min: 203, max: 206, size: "13.5C", euro: "32",   narrow: null,        standard: null,        wide: null,         doubleWide: null         },
    { min: 207, max: 216, size: "1",     euro: "33",   narrow: null,        standard: null,        wide: null,         doubleWide: null         },
    { min: 217, max: 218, size: "2",     euro: "34",   narrow: null,        standard: null,        wide: null,         doubleWide: null         },
    { min: 219, max: 223, size: "2.5",   euro: "34.5", narrow: null,        standard: null,        wide: null,         doubleWide: null         },
    { min: 224, max: 228, size: "3",     euro: "35",   narrow: [79, 83],    standard: [84, 93],    wide: [94, 98],     doubleWide: [99, 103]    },
    { min: 229, max: 231, size: "3.5",   euro: "35.5", narrow: [80, 84],    standard: [85, 94],    wide: [95, 99],     doubleWide: [100, 104]   },
    { min: 232, max: 237, size: "4",     euro: "36",   narrow: [82, 86],    standard: [86, 96],    wide: [97, 101],    doubleWide: [102, 106]   },
    { min: 238, max: 241, size: "4.5",   euro: "36.5", narrow: [82, 86],    standard: [87, 97],    wide: [98, 102],    doubleWide: [103, 107]   },
    { min: 242, max: 245, size: "5",     euro: "37",   narrow: [84, 88],    standard: [89, 98],    wide: [99, 103],    doubleWide: [104, 108]   },
    { min: 246, max: 250, size: "5.5",   euro: "37.5", narrow: [85, 89],    standard: [90, 100],   wide: [101, 105],   doubleWide: [106, 110]   },
    { min: 251, max: 253, size: "6",     euro: "38",   narrow: [86, 90],    standard: [91, 101],   wide: [102, 106],   doubleWide: [107, 111]   },
    { min: 254, max: 259, size: "6.5",   euro: "38.5", narrow: [87, 91],    standard: [92, 103],   wide: [104, 108],   doubleWide: [109, 113]   },
    { min: 260, max: 262, size: "7",     euro: "39",   narrow: [89, 93],    standard: [94, 104],   wide: [105, 109],   doubleWide: [110, 114]   },
    { min: 263, max: 268, size: "7.5",   euro: "40",   narrow: [90, 94],    standard: [95, 105],   wide: [106, 110],   doubleWide: [111, 115]   },
    { min: 269, max: 272, size: "8",     euro: "41",   narrow: [91, 95],    standard: [96, 107],   wide: [108, 112],   doubleWide: [113, 117]   },
    { min: 273, max: 277, size: "8.5",   euro: "41.5", narrow: [92, 96],    standard: [97, 108],   wide: [109, 113],   doubleWide: [114, 118]   },
    { min: 278, max: 281, size: "9",     euro: "42",   narrow: [94, 98],    standard: [99, 110],   wide: [111, 115],   doubleWide: [116, 120]   },
    { min: 282, max: 286, size: "9.5",   euro: "43",   narrow: [95, 99],    standard: [100, 111],  wide: [112, 116],   doubleWide: [117, 121]   },
    { min: 287, max: 290, size: "10",    euro: "44",   narrow: [96, 100],   standard: [101, 112],  wide: [113, 117],   doubleWide: [118, 122]   },
    { min: 291, max: 295, size: "10.5",  euro: "45",   narrow: [97, 101],   standard: [102, 114],  wide: [115, 119],   doubleWide: [120, 124]   },
    { min: 296, max: 298, size: "11",    euro: "46",   narrow: [99, 103],   standard: [104, 115],  wide: [116, 120],   doubleWide: [121, 125]   },
    { min: 299, max: 302, size: "11.5",  euro: "47",   narrow: [100, 104],  standard: [105, 117],  wide: [118, 122],   doubleWide: [123, 127]   },
    { min: 303, max: 304, size: "12",    euro: "48",   narrow: [101, 105],  standard: [106, 118],  wide: [119, 123],   doubleWide: [124, 128]   },
    { min: 305, max: 311, size: "12.5",  euro: "49",   narrow: [102, 106],  standard: [107, 119],  wide: [120, 124],   doubleWide: [125, 129]   },
    { min: 312, max: 313, size: "13",    euro: "50",   narrow: [104, 108],  standard: [109, 121],  wide: [122, 126],   doubleWide: [127, 131]   }
  ];
  return chart.find(r => length >= r.min && length <= r.max) || { size: "N/A", euro: "N/A" };
}

// --- Chart 4: All Other Inline & Roller Boots ---
// Same width-fit structure as Parkstar (Narrow / Standard / Wide / Double Wide),
// but with a few special cases:
//   - Size 9C (150–162):   Standard "75 and under"  → [0, 75];  no Narrow, no Double Wide.
//   - Size 10C (163–171):  Narrow   "69 and under"  → [0, 69];  no Double Wide.
//   - Sizes 9C → 13.5C:    no Double Wide entry.
function initGeneralInlineRoller(container = document) {
  setupHandlers(runGeneralInlineRoller, container);
}

function runGeneralInlineRoller(container = document) {
  const leftLengthInput  = container.querySelector("#left-foot-length");
  const rightLengthInput = container.querySelector("#right-foot-length");
  const leftWidthInput   = container.querySelector("#left-foot-width");
  const rightWidthInput  = container.querySelector("#right-foot-width");

  const leftBont  = container.querySelector(".left-bont");
  const rightBont = container.querySelector(".right-bont");
  const leftEuro  = container.querySelector(".left-euro");
  const rightEuro = container.querySelector(".right-euro");
  const leftWidth  = container.querySelector(".left-width");
  const rightWidth = container.querySelector(".right-width");

  if (!leftLengthInput || !rightLengthInput || !leftBont || !rightBont) return;

  const left  = getGeneralSize(+leftLengthInput.value);
  const right = getGeneralSize(+rightLengthInput.value);

  leftBont.textContent  = `${left.size}`;
  rightBont.textContent = `${right.size}`;

  if (leftEuro)  leftEuro.textContent  = `${left.euro}`;
  if (rightEuro) rightEuro.textContent = `${right.euro}`;

  if (leftWidth) {
    const w = leftWidthInput && leftWidthInput.value !== "" ? +leftWidthInput.value : null;
    leftWidth.textContent = classifyWidthByFits(w, left);
  }
  if (rightWidth) {
    const w = rightWidthInput && rightWidthInput.value !== "" ? +rightWidthInput.value : null;
    rightWidth.textContent = classifyWidthByFits(w, right);
  }
}

function getGeneralSize(length) {
  // Mirrors the table on the General Inline / Roller size-chart page.
  // "X and under" rows are normalised to [0, X] for the lookup.
  const chart = [
    { min: 150, max: 162, size: "9 C",    euro: "27",   narrow: null,       standard: [0, 75],    wide: [76, 80],   doubleWide: null       },
    { min: 163, max: 171, size: "10 C",   euro: "28",   narrow: [0, 69],    standard: [70, 78],   wide: [79, 83],   doubleWide: null       },
    { min: 172, max: 179, size: "11 C",   euro: "29",   narrow: [68, 71],   standard: [72, 80],   wide: [81, 85],   doubleWide: null       },
    { min: 180, max: 188, size: "12 C",   euro: "30",   narrow: [69, 73],   standard: [74, 82],   wide: [83, 87],   doubleWide: null       },
    { min: 189, max: 198, size: "13 C",   euro: "31",   narrow: [70, 74],   standard: [75, 84],   wide: [85, 89],   doubleWide: null       },
    { min: 199, max: 203, size: "13.5 C", euro: "32",   narrow: [72, 76],   standard: [77, 86],   wide: [87, 91],   doubleWide: null       },
    { min: 204, max: 207, size: "1",      euro: "33",   narrow: [73, 77],   standard: [78, 87],   wide: [88, 92],   doubleWide: [93, 97]   },
    { min: 208, max: 215, size: "2",      euro: "34",   narrow: [76, 80],   standard: [81, 90],   wide: [91, 95],   doubleWide: [96, 100]  },
    { min: 216, max: 219, size: "2.5",    euro: "34.5", narrow: [77, 81],   standard: [82, 91],   wide: [92, 96],   doubleWide: [97, 101]  },
    { min: 220, max: 224, size: "3",      euro: "35",   narrow: [79, 83],   standard: [84, 93],   wide: [94, 98],   doubleWide: [99, 103]  },
    { min: 225, max: 228, size: "3.5",    euro: "35.5", narrow: [80, 84],   standard: [85, 94],   wide: [95, 99],   doubleWide: [100, 104] },
    { min: 229, max: 232, size: "4",      euro: "36",   narrow: [82, 86],   standard: [86, 96],   wide: [97, 101],  doubleWide: [102, 106] },
    { min: 233, max: 237, size: "4.5",    euro: "36.5", narrow: [82, 86],   standard: [87, 97],   wide: [98, 102],  doubleWide: [103, 107] },
    { min: 238, max: 241, size: "5",      euro: "37",   narrow: [84, 88],   standard: [89, 98],   wide: [99, 103],  doubleWide: [104, 108] },
    { min: 242, max: 246, size: "5.5",    euro: "37.5", narrow: [85, 89],   standard: [90, 100],  wide: [101, 105], doubleWide: [106, 110] },
    { min: 247, max: 250, size: "6",      euro: "38",   narrow: [86, 90],   standard: [91, 101],  wide: [102, 106], doubleWide: [107, 111] },
    { min: 251, max: 255, size: "6.5",    euro: "38.5", narrow: [87, 91],   standard: [92, 103],  wide: [104, 108], doubleWide: [109, 113] },
    { min: 256, max: 259, size: "7",      euro: "39",   narrow: [89, 93],   standard: [94, 104],  wide: [105, 109], doubleWide: [110, 114] },
    { min: 260, max: 264, size: "7.5",    euro: "40",   narrow: [90, 94],   standard: [95, 105],  wide: [106, 110], doubleWide: [111, 115] },
    { min: 265, max: 268, size: "8",      euro: "41",   narrow: [91, 95],   standard: [96, 107],  wide: [108, 112], doubleWide: [113, 117] },
    { min: 269, max: 272, size: "8.5",    euro: "41.5", narrow: [92, 96],   standard: [97, 108],  wide: [109, 113], doubleWide: [114, 118] },
    { min: 273, max: 277, size: "9",      euro: "42",   narrow: [94, 98],   standard: [99, 110],  wide: [111, 115], doubleWide: [116, 120] },
    { min: 278, max: 281, size: "9.5",    euro: "43",   narrow: [95, 99],   standard: [100, 111], wide: [112, 116], doubleWide: [117, 121] },
    { min: 282, max: 286, size: "10",     euro: "44",   narrow: [96, 100],  standard: [101, 112], wide: [113, 117], doubleWide: [118, 122] },
    { min: 287, max: 290, size: "10.5",   euro: "45",   narrow: [97, 101],  standard: [102, 114], wide: [115, 119], doubleWide: [120, 124] },
    { min: 291, max: 295, size: "11",     euro: "46",   narrow: [99, 103],  standard: [104, 115], wide: [116, 120], doubleWide: [121, 125] },
    { min: 296, max: 299, size: "11.5",   euro: "47",   narrow: [100, 104], standard: [105, 117], wide: [118, 122], doubleWide: [123, 127] },
    { min: 300, max: 304, size: "12",     euro: "48",   narrow: [101, 105], standard: [106, 118], wide: [119, 123], doubleWide: [124, 128] },
    { min: 305, max: 308, size: "12.5",   euro: "49",   narrow: [102, 106], standard: [107, 119], wide: [120, 124], doubleWide: [125, 129] },
    { min: 309, max: 313, size: "13",     euro: "50",   narrow: [104, 108], standard: [109, 121], wide: [122, 126], doubleWide: [127, 131] },
    { min: 314, max: 317, size: "13.5",   euro: "51",   narrow: [105, 109], standard: [110, 122], wide: [123, 127], doubleWide: [128, 132] }
  ];
  return chart.find(r => length >= r.min && length <= r.max) || { size: "N/A", euro: "N/A" };
}

// --- Chart 5: Spin Boot ---
function initSpinBoot(container = document) {
  setupHandlers(runSpinBoot, container);
}

function runSpinBoot(container = document) {
  const leftInput = container.querySelector("#left-foot-length");
  const rightInput = container.querySelector("#right-foot-length");
  const leftBont = container.querySelector(".left-bont");
  const rightBont = container.querySelector(".right-bont");
  const leftStandard = container.querySelector(".left-standard-width");
  const rightStandard = container.querySelector(".right-standard-width");
  const leftWide = container.querySelector(".left-wide-width");
  const rightWide = container.querySelector(".right-wide-width");

  if (!leftInput || !rightInput || !leftBont || !rightBont) return;

  const left = getSpinBootSize(+leftInput.value);
  const right = getSpinBootSize(+rightInput.value);

  leftBont.textContent = `${left.size}`;
  rightBont.textContent = `${right.size}`;

  if (leftStandard) leftStandard.textContent = `Standard: ${left.standardWidth} mm`;
  if (rightStandard) rightStandard.textContent = `Standard: ${right.standardWidth} mm`;
  if (leftWide) leftWide.textContent = `Wide: ${left.wideWidth} mm`;
  if (rightWide) rightWide.textContent = `Wide: ${right.wideWidth} mm`;
}

function getSpinBootSize(length) {
  const chart = [
    { min: 175, max: 179, size: "180", standardWidth: "67 - 71",  wideWidth: "71 - 75"  },
    { min: 180, max: 184, size: "185", standardWidth: "70 - 74",  wideWidth: "74 - 78"  },
    { min: 185, max: 189, size: "190", standardWidth: "71 - 75",  wideWidth: "75 - 79"  },
    { min: 190, max: 194, size: "195", standardWidth: "72 - 76",  wideWidth: "76 - 80"  },
    { min: 195, max: 199, size: "200", standardWidth: "73 - 77",  wideWidth: "77 - 81"  },
    { min: 200, max: 204, size: "205", standardWidth: "76 - 80",  wideWidth: "80 - 84"  },
    { min: 205, max: 209, size: "210", standardWidth: "78 - 87",  wideWidth: "82 - 91"  },
    { min: 210, max: 214, size: "215", standardWidth: "79 - 88",  wideWidth: "83 - 92"  },
    { min: 215, max: 219, size: "220", standardWidth: "81 - 90",  wideWidth: "85 - 94"  },
    { min: 220, max: 224, size: "225", standardWidth: "82 - 91",  wideWidth: "86 - 95"  },
    { min: 225, max: 229, size: "230", standardWidth: "84 - 93",  wideWidth: "88 - 97"  },
    { min: 230, max: 234, size: "235", standardWidth: "86 - 96",  wideWidth: "90 - 100" },
    { min: 235, max: 239, size: "240", standardWidth: "87 - 97",  wideWidth: "91 - 101" },
    { min: 240, max: 244, size: "245", standardWidth: "89 - 98",  wideWidth: "93 - 102" },
    { min: 245, max: 249, size: "250", standardWidth: "90 - 100", wideWidth: "94 - 104" },
    { min: 250, max: 254, size: "255", standardWidth: "91 - 101", wideWidth: "95 - 105" },
    { min: 255, max: 259, size: "260", standardWidth: "92 - 103", wideWidth: "96 - 107" },
    { min: 260, max: 264, size: "265", standardWidth: "94 - 104", wideWidth: "98 - 108" },
    { min: 265, max: 269, size: "270", standardWidth: "95 - 105", wideWidth: "99 - 109" },
    { min: 270, max: 274, size: "275", standardWidth: "97 - 108", wideWidth: "101 - 112" },
    { min: 275, max: 279, size: "280", standardWidth: "99 - 110", wideWidth: "103 - 114" },
    { min: 280, max: 284, size: "285", standardWidth: "100 - 111", wideWidth: "104 - 115" },
    { min: 285, max: 289, size: "290", standardWidth: "101 - 112", wideWidth: "105 - 116" },
    { min: 290, max: 294, size: "295", standardWidth: "102 - 114", wideWidth: "106 - 118" },
    { min: 295, max: 299, size: "300", standardWidth: "104 - 115", wideWidth: "108 - 119" },
    { min: 300, max: 304, size: "305", standardWidth: "106 - 118", wideWidth: "110 - 122" },
    { min: 305, max: 309, size: "310", standardWidth: "107 - 119", wideWidth: "111 - 123" },
    { min: 310, max: 314, size: "315", standardWidth: "109 - 121", wideWidth: "113 - 125" }
  ];
  return chart.find(r => length >= r.min && length <= r.max) || { size: "N/A", standardWidth: "N/A", wideWidth: "N/A" };
}

// --- Utility Functions ---

// Global function to manually initialize chart by type (for external use)
window.initBontChart = function(chartType, container) {
  initializeChartByType(chartType, container);
};

// Export chart types for external reference
window.BONT_CHART_TYPES = CHART_TYPES;

// Debug and enhanced tooltip handling
console.log('[BONT] Custom.js loaded successfully');

// Enhanced tooltip event handler with fallbacks
document.addEventListener('tooltip:open', (e) => {
  console.log('[BONT] Tooltip event caught:', e.detail);

  const ctx = e.detail.context;
  if (ctx !== 'size-chart') {
    console.log('[BONT] Not a size chart tooltip, ignoring');
    return;
  }

  setTimeout(() => {
    const tooltip = document.querySelector('tool-tip[data-tool-tip="size-chart"]');
    if (tooltip) {
      console.log('[BONT] Size chart tooltip found:', tooltip);

      const chartContainer = tooltip.querySelector('[data-chart-type]');
      const chartType = chartContainer ? chartContainer.getAttribute('data-chart-type') : 'general-inline';

      console.log('[BONT] Initializing chart type:', chartType);
      initializeChartByType(chartType, tooltip);
    } else {
      console.log('[BONT] Size chart tooltip not found in DOM');
    }
  }, 200);
});

// Additional failsafe: listen for size chart button clicks directly
document.addEventListener('click', (e) => {
  const sizeChartTrigger = e.target.closest('tool-tip-trigger[data-tool-tip="size-chart"]');
  if (sizeChartTrigger) {
    console.log('[BONT] Size chart trigger clicked directly');

    setTimeout(() => {
      const tooltip = document.querySelector('tool-tip[data-tool-tip="size-chart"]');
      if (tooltip) {
        console.log('[BONT] Processing size chart from direct click');
        initializeChartByType('general-inline', tooltip);
      }
    }, 300);
  }
});

// Universal size chart detector - checks for any tooltip content that looks like a size chart
function detectAndInitializeSizeCharts() {
  const openTooltips = document.querySelectorAll('tool-tip[data-tool-tip-open="true"]');

  openTooltips.forEach(tooltip => {
    if (tooltip.hasAttribute('data-bont-initialized')) return;

    const hasFootLengthInputs = tooltip.querySelector('#left-foot-length, #right-foot-length');
    const hasSizeChartText = tooltip.textContent.toLowerCase().includes('size') &&
                           tooltip.textContent.toLowerCase().includes('chart');
    const hasGoButton = tooltip.querySelector('.goButton');

    if (hasFootLengthInputs || (hasSizeChartText && hasGoButton)) {
      console.log('[BONT] Found uninitialized size chart via content detection:', tooltip);
      tooltip.setAttribute('data-bont-initialized', 'true');

      const content = tooltip.textContent.toLowerCase();
      let chartType = 'general-inline';

      if (content.includes('slipstream') || content.includes('vaypor')) {
        chartType = 'slipstream-vaypor';
      } else if (content.includes('eclipse')) {
        chartType = 'eclipse';
      } else if (content.includes('parkstar') || content.includes('prostar')) {
        chartType = 'parkstar-prostar';
      } else if (content.includes('spin')) {
        chartType = 'spin-boot';
      }

      console.log('[BONT] Auto-detected chart type:', chartType);
      initializeChartByType(chartType, tooltip);
    }
  });
}

// Run the detector periodically to catch any tooltips that weren't caught by events
setInterval(detectAndInitializeSizeCharts, 1000);

// Also run the detector when DOM mutations occur (tooltips opening)
const tooltipWatcher = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1 && node.tagName === 'TOOL-TIP') {
        console.log('[BONT] New tooltip detected via mutation observer');
        setTimeout(detectAndInitializeSizeCharts, 100);
      }
    });
  });
});

tooltipWatcher.observe(document.body, { childList: true, subtree: true });